/**
 * Games Management Page
 * View and manage active games
 */

import React, { useState, useEffect } from 'react';
import { useAdmin } from '../AdminApp';
import { toast } from 'sonner';
import {
  Gamepad2,
  Users,
  Trophy,
  Clock,
  RefreshCw,
  Play,
  Pause,
  Flag
} from 'lucide-react';

// Emerald Icon Component
const EmeraldIcon = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="emeraldGradientGames" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#34d399" />
        <stop offset="50%" stopColor="#10b981" />
        <stop offset="100%" stopColor="#059669" />
      </linearGradient>
    </defs>
    <path d="M12 2L4 7V17L12 22L20 17V7L12 2Z" fill="url(#emeraldGradientGames)" />
    <path d="M12 2L4 7L12 12L20 7L12 2Z" fill="#6ee7b7" fillOpacity="0.6" />
    <path d="M12 12V22L4 17V7L12 12Z" fill="#059669" fillOpacity="0.4" />
  </svg>
);

const phaseColors = {
  lobby: 'bg-gray-100 text-gray-700',
  bidding: 'bg-blue-100 text-blue-700',
  playing: 'bg-emerald-100 text-emerald-700',
  finished: 'bg-amber-100 text-amber-700'
};

const GamesPage = () => {
  const { api } = useAdmin();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGames();
  }, []);

  const fetchGames = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/games/active');
      setGames(response.data.games);
    } catch (error) {
      toast.error('Failed to load games');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Game Management</h1>
          <p className="text-gray-500 text-sm mt-1">Monitor active Spades games</p>
        </div>
        <button
          onClick={fetchGames}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-50 rounded-lg">
              <Play className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {games.filter(g => g.phase === 'playing').length}
              </p>
              <p className="text-sm text-gray-500">In Progress</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Pause className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {games.filter(g => g.phase === 'bidding').length}
              </p>
              <p className="text-sm text-gray-500">Bidding</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-50 rounded-lg">
              <Users className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {games.filter(g => g.phase === 'lobby').length}
              </p>
              <p className="text-sm text-gray-500">In Lobby</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-50 rounded-lg">
              <Gamepad2 className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{games.length}</p>
              <p className="text-sm text-gray-500">Total Active</p>
            </div>
          </div>
        </div>
      </div>

      {/* Games List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">Active Games</h2>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent"></div>
          </div>
        ) : games.length === 0 ? (
          <div className="text-center py-12">
            <Gamepad2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No active games at the moment</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs font-semibold text-gray-500 uppercase">
                  <th className="py-3 px-4">Game ID</th>
                  <th className="py-3 px-4">Phase</th>
                  <th className="py-3 px-4">Players</th>
                  <th className="py-3 px-4">Entry</th>
                  <th className="py-3 px-4">Round</th>
                  <th className="py-3 px-4">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {games.map((game) => (
                  <tr key={game.id} className="hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <span className="font-mono text-sm text-gray-600">
                        {game.id?.substring(0, 8)}...
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${phaseColors[game.phase] || 'bg-gray-100 text-gray-700'}`}>
                        {game.phase}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {game.players?.length || 0} / {game.max_players || 4}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm text-emerald-600 font-medium flex items-center gap-1">
                        {game.wager_amount || 0} <EmeraldIcon className="h-4 w-4" />
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      Round {game.current_round || 1}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {game.created_at ? new Date(game.created_at).toLocaleString() : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default GamesPage;
